import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { 
  CreditCard, 
  FileText, 
  Zap, 
  Shield, 
  Users, 
  BarChart3, 
  ArrowRight,
  Check,
  Globe,
  Lock,
  Sparkles,
  TrendingUp,
  Wallet,
  ChevronRight,
  Star,
  Play,
  Banknote,
  X,
  Clock
} from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "/logo-alphabit.png";
const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function LandingPage() {
  const { t, currentLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState(null);

  // Track page visit (skip if admin is logged in)
  useEffect(() => {
    try {
      const token = localStorage.getItem('alphabit_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'super_admin') return;
      }
    } catch {}
    axios.post(`${API}/api/analytics/track?page=/&site=main`).catch(() => {});
  }, []);
  
  // Feature details for the dialog
  const featureDetails = {
    paymentLinks: {
      it: {
        benefits: [
          "Crea link in pochi secondi",
          "QR code automatico incluso",
          "Condividi via WhatsApp, Email, SMS",
          "Personalizza importo e descrizione",
          "Traccia ogni pagamento in tempo reale"
        ],
        cta: "Crea il tuo primo link"
      },
      en: {
        benefits: [
          "Create links in seconds",
          "Automatic QR code included",
          "Share via WhatsApp, Email, SMS",
          "Customize amount and description",
          "Track every payment in real-time"
        ],
        cta: "Create your first link"
      }
    },
    invoicing: {
      it: {
        benefits: [
          "Fatture professionali PDF",
          "Link di pagamento integrato",
          "Numerazione automatica",
          "Logo e branding personalizzato",
          "Invio automatico via email"
        ],
        cta: "Inizia a fatturare"
      },
      en: {
        benefits: [
          "Professional PDF invoices",
          "Integrated payment link",
          "Automatic numbering",
          "Custom logo and branding",
          "Automatic email sending"
        ],
        cta: "Start invoicing"
      }
    },
    api: {
      it: {
        benefits: [
          "API REST semplice e documentata",
          "Webhook in tempo reale",
          "Sandbox per test",
          "SDK e esempi di codice",
          "Supporto multi-skin"
        ],
        cta: "Leggi la documentazione"
      },
      en: {
        benefits: [
          "Simple and documented REST API",
          "Real-time webhooks",
          "Sandbox for testing",
          "SDK and code examples",
          "Multi-skin support"
        ],
        cta: "Read documentation"
      }
    },
    security: {
      it: {
        benefits: [
          "Crittografia end-to-end",
          "Conformità PCI DSS",
          "Rate limiting e protezione DDoS",
          "Autenticazione a due fattori",
          "Monitoraggio 24/7"
        ],
        cta: "Scopri le nostre misure"
      },
      en: {
        benefits: [
          "End-to-end encryption",
          "PCI DSS compliance",
          "Rate limiting and DDoS protection",
          "Two-factor authentication",
          "24/7 monitoring"
        ],
        cta: "Learn about our measures"
      }
    },
    agents: {
      it: {
        benefits: [
          "Commissioni su ogni transazione",
          "Dashboard dedicata",
          "Gestione merchant illimitati",
          "Report dettagliati",
          "Prelievi facili e veloci"
        ],
        cta: "Diventa agente"
      },
      en: {
        benefits: [
          "Commissions on every transaction",
          "Dedicated dashboard",
          "Unlimited merchant management",
          "Detailed reports",
          "Easy and fast withdrawals"
        ],
        cta: "Become an agent"
      }
    },
    analytics: {
      it: {
        benefits: [
          "Dashboard in tempo reale",
          "Grafici e statistiche",
          "Export dati CSV/PDF",
          "Filtri avanzati",
          "Confronto periodi"
        ],
        cta: "Vedi la demo"
      },
      en: {
        benefits: [
          "Real-time dashboard",
          "Charts and statistics",
          "CSV/PDF data export",
          "Advanced filters",
          "Period comparison"
        ],
        cta: "See the demo"
      }
    }
  };
  
  const features = [
    {
      icon: CreditCard,
      title: t('landing.features.paymentLinks.title'),
      description: t('landing.features.paymentLinks.description'),
      color: 'from-violet-500 to-purple-600',
      bgColor: 'bg-violet-100',
      textColor: 'text-violet-600',
      key: 'paymentLinks',
      ctaLink: '/register'
    },
    {
      icon: FileText,
      title: t('landing.features.invoicing.title'),
      description: t('landing.features.invoicing.description'),
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
      key: 'invoicing',
      ctaLink: '/register'
    },
    {
      icon: Zap,
      title: t('landing.features.api.title'),
      description: t('landing.features.api.description'),
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-600',
      key: 'api',
      ctaLink: '/docs'
    },
    {
      icon: Shield,
      title: t('landing.features.security.title'),
      description: t('landing.features.security.description'),
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-100',
      textColor: 'text-emerald-600',
      key: 'security',
      ctaLink: '/docs'
    },
    {
      icon: Users,
      title: t('landing.features.agents.title'),
      description: t('landing.features.agents.description'),
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-100',
      textColor: 'text-pink-600',
      key: 'agents',
      ctaLink: '/register'
    },
    {
      icon: BarChart3,
      title: t('landing.features.analytics.title'),
      description: t('landing.features.analytics.description'),
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-600',
      key: 'analytics',
      ctaLink: '/register'
    }
  ];

  const benefits = [
    t('landing.benefits.noSetupFee'),
    t('landing.benefits.transparentFees'),
    t('landing.benefits.multiCurrency'),
    t('landing.benefits.whiteLabel'),
    t('landing.benefits.customDashboard'),
    t('landing.benefits.realTimeWebhook')
  ];

  const stats = [
    { value: '99.9%', label: 'Uptime', icon: TrendingUp },
    { value: '< 1s', label: 'Tempo risposta', icon: Zap },
    { value: '3+', label: 'Gateway', icon: CreditCard },
    { value: '7', label: 'Lingue', icon: Globe }
  ];
  
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b ${
        isDark 
          ? 'bg-[#0B0F2A]/90 border-purple-500/20' 
          : 'bg-white/80 border-slate-200/50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <img src={LOGO_URL} alt="AlphaBit" className={`w-9 h-9 rounded-xl shadow-lg ${isDark ? 'shadow-purple-500/30' : ''}`} />
              <span className={`font-heading font-bold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>Alpha<span className={isDark ? 'text-[#9D4DFF]' : 'text-violet-600'}>Bit</span></span>
            </Link>
            
            {/* Right side controls */}
            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <LanguageSelector className="scale-90" />
              
              {/* Login button */}
              <Link to="/login">
                <Button variant="ghost" size="sm" className={`px-2 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                  {t('common.login')}
                </Button>
              </Link>
              
              {/* Desktop: Register button */}
              <Link to="/register" className="hidden md:block">
                <Button size="sm" className={`gap-1 ${isDark ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF] shadow-lg shadow-purple-500/40' : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25'}`} data-testid="nav-register">
                  <Sparkles className="w-3 h-3" />
                  {t('landing.startFree')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={`pt-20 sm:pt-24 pb-20 px-4 sm:px-6 relative overflow-hidden ${
        isDark 
          ? 'bg-gradient-to-br from-[#12002F] via-[#2A005F] to-[#0B0F2A]' 
          : 'bg-gradient-to-br from-slate-50 via-white to-slate-100'
      }`}>
        {/* Background decorations - Neon glow effects */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          {isDark ? (
            <>
              <div className="absolute top-20 left-10 w-[500px] h-[500px] bg-purple-600 rounded-full mix-blend-screen filter blur-[150px] opacity-30"></div>
              <div className="absolute top-40 right-10 w-[600px] h-[600px] bg-[#9D4DFF] rounded-full mix-blend-screen filter blur-[150px] opacity-25"></div>
              <div className="absolute bottom-20 left-1/3 w-[400px] h-[400px] bg-[#6A00FF] rounded-full mix-blend-screen filter blur-[120px] opacity-30"></div>
            </>
          ) : (
            <>
              <div className="absolute top-20 left-10 w-72 h-72 bg-violet-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
              <div className="absolute top-40 right-10 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse delay-1000"></div>
              <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse delay-500"></div>
            </>
          )}
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            {/* Theme Toggle - Centered above badge */}
            <button 
              onClick={toggleTheme}
              className={`mb-2 w-8 h-8 mx-auto flex items-center justify-center rounded-full transition-all hover:scale-110 ${
                isDark 
                  ? 'bg-white/10 text-yellow-400 hover:bg-white/20 border border-purple-500/30' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
              }`}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
            
            <Badge className={`mb-4 px-4 py-2 text-sm font-medium backdrop-blur-sm ${
              isDark 
                ? 'bg-white/10 text-[#9D4DFF] hover:bg-white/15 border border-purple-500/30' 
                : 'bg-violet-100 text-violet-700 hover:bg-violet-100'
            }`}>
              <Zap className="w-4 h-4 mr-2" />
              {t('landing.smartPaymentGateway')}
            </Badge>
            
            <h1 className={`font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('landing.heroTitle')}{' '}
              <span className={isDark 
                ? 'bg-gradient-to-r from-[#9D4DFF] via-[#B76EFF] to-[#6A00FF] bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(157,77,255,0.5)]'
                : 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent'
              }>
                {t('landing.heroTitleHighlight')}
              </span>
            </h1>
            
            <p className={`text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
              {t('landing.heroSubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link to="/register">
                <Button size="lg" className={`w-full sm:w-auto gap-2 px-8 py-6 text-lg hover:scale-105 transition-all duration-300 ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF] shadow-[0_0_40px_rgba(157,77,255,0.5)]' 
                    : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-xl shadow-violet-500/30'
                }`} data-testid="hero-cta">
                  {t('landing.getStarted')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className={`w-full sm:w-auto px-8 py-6 text-lg gap-2 ${
                  isDark 
                    ? 'border-2 border-purple-500/50 text-white hover:border-[#9D4DFF] hover:bg-purple-500/10 backdrop-blur-sm' 
                    : 'border-2 border-slate-300 text-slate-700 hover:border-violet-400 hover:bg-violet-50'
                }`} data-testid="hero-login">
                  <Play className="w-5 h-5" />
                  {t('landing.accessDashboard')}
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {stats.map((stat, index) => (
                <Card key={index} className={`backdrop-blur transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 border-purple-500/20 hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/20' 
                    : 'bg-white/80 border-slate-200/50 hover:shadow-lg'
                }`}>
                  <CardContent className="p-4 text-center">
                    <stat.icon className={`w-6 h-6 mx-auto mb-2 ${isDark ? 'text-[#9D4DFF]' : 'text-violet-600'}`} />
                    <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Demo Card */}
          <div className="mt-20 max-w-md mx-auto lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:mt-0">
            <Card className={`backdrop-blur-xl overflow-hidden ${
              isDark 
                ? 'bg-[#0F0F2F]/80 shadow-2xl shadow-purple-500/20 border border-purple-500/30' 
                : 'bg-white shadow-2xl shadow-slate-200/50 border-0'
            }`}>
              <div className={`p-4 ${isDark ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF]' : 'bg-gradient-to-r from-violet-600 to-purple-600'}`}>
                <div className="flex items-center justify-between text-white">
                  <span className="text-sm font-medium">💳 Link di Pagamento</span>
                  <Badge className="bg-white/20 text-white hover:bg-white/30">Live</Badge>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                    isDark 
                      ? 'bg-gradient-to-br from-[#6A00FF] to-[#9D4DFF] shadow-purple-500/30' 
                      : 'bg-gradient-to-br from-violet-500 to-purple-600'
                  }`}>
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Premium Service</p>
                    <p className={`font-heading text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>€ 150,00</p>
                  </div>
                </div>
                <Button className={`w-full mb-4 ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF] shadow-lg shadow-purple-500/30' 
                    : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700'
                }`}>
                  {t('payment.payWithPaypal')}
                </Button>
                <div className={`flex items-center justify-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  <Lock className="w-4 h-4" />
                  {t('branding.securePayment')}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`py-20 px-4 sm:px-6 ${isDark ? 'bg-[#09081A]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className={isDark ? 'mb-4 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30' : 'mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}>
              <Star className="w-4 h-4 mr-2" />
              Funzionalità
            </Badge>
            <h2 className={`font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('landing.features.title')}
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('landing.features.subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className={`group transition-all duration-500 hover:-translate-y-2 overflow-hidden cursor-pointer ${
                  isDark 
                    ? 'bg-[#0F0F2F] border-purple-500/20 hover:border-purple-500/50 shadow-lg shadow-purple-500/10 hover:shadow-xl hover:shadow-purple-500/20' 
                    : 'border-0 shadow-lg hover:shadow-2xl'
                }`}
                data-testid={`feature-${index}`}
                onClick={() => setSelectedFeature(feature)}
              >
                <CardContent className="p-8 relative">
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${feature.color} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 group-hover:opacity-20 transition-all duration-500`}></div>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg ${isDark ? 'shadow-purple-500/30' : ''}`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className={`font-heading text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {feature.title}
                  </h3>
                  <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    {feature.description}
                  </p>
                  <div className={`mt-6 flex items-center text-sm font-medium ${isDark ? 'text-[#9D4DFF] group-hover:text-[#B76EFF]' : 'text-violet-600 group-hover:text-violet-700'}`}>
                    {t('landing.features.learnMore')}
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Feature Detail Dialog */}
          <Dialog open={!!selectedFeature} onOpenChange={(open) => !open && setSelectedFeature(null)}>
            <DialogContent className={`sm:max-w-lg ${isDark ? 'bg-[#0F0F2F] border-purple-500/30 text-white' : ''}`}>
              {selectedFeature && (
                <>
                  <DialogHeader>
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${selectedFeature.color} flex items-center justify-center mb-4 shadow-lg ${isDark ? 'shadow-purple-500/30' : ''}`}>
                      <selectedFeature.icon className="w-8 h-8 text-white" />
                    </div>
                    <DialogTitle className={`text-2xl ${isDark ? 'text-white' : ''}`}>{selectedFeature.title}</DialogTitle>
                    <DialogDescription className={`text-base ${isDark ? 'text-gray-400' : ''}`}>
                      {selectedFeature.description}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="mt-4 space-y-3">
                    {featureDetails[selectedFeature.key]?.[currentLanguage]?.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${selectedFeature.color} flex items-center justify-center flex-shrink-0`}>
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <span className={isDark ? 'text-gray-300' : 'text-slate-700'}>{benefit}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 flex gap-3">
                    <Button 
                      className={`flex-1 bg-gradient-to-r ${selectedFeature.color} hover:opacity-90`}
                      onClick={() => {
                        setSelectedFeature(null);
                        navigate(selectedFeature.ctaLink);
                      }}
                    >
                      {featureDetails[selectedFeature.key]?.[currentLanguage]?.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <Button 
                      variant="outline"
                      className={isDark ? 'border-purple-500/50 text-white hover:bg-purple-500/10' : ''}
                      onClick={() => setSelectedFeature(null)}
                    >
                      {currentLanguage === 'it' ? 'Chiudi' : 'Close'}
                    </Button>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* USDA Pay - Premium Section */}
      <section className={`py-20 px-4 sm:px-6 relative overflow-hidden ${
        isDark 
          ? 'bg-gradient-to-br from-[#001A0E] via-[#0B2518] to-[#0B0F2A]' 
          : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-white'
      }`}>
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {isDark ? (
            <>
              <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-emerald-600 rounded-full mix-blend-screen filter blur-[180px] opacity-15"></div>
              <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-teal-500 rounded-full mix-blend-screen filter blur-[150px] opacity-15"></div>
            </>
          ) : (
            <>
              <div className="absolute top-10 right-10 w-72 h-72 bg-emerald-200 rounded-full filter blur-3xl opacity-40"></div>
              <div className="absolute bottom-10 left-10 w-80 h-80 bg-teal-200 rounded-full filter blur-3xl opacity-30"></div>
            </>
          )}
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div>
              <Badge className={`mb-4 ${isDark ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-100 text-emerald-700'}`}>
                <Zap className="w-4 h-4 mr-2" />
                {currentLanguage === 'it' ? 'Nuovo Gateway' : 'New Gateway'}
              </Badge>
              <h2 className={`font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {currentLanguage === 'it' ? 'Accetta pagamenti in' : 'Accept payments in'}{' '}
                <span className={isDark 
                  ? 'bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent' 
                  : 'bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent'
                }>USDA</span>
              </h2>
              <p className={`text-base sm:text-lg mb-8 leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                {currentLanguage === 'it' 
                  ? 'La stablecoin di nuova generazione ancorata 1:1 al dollaro, su Polygon. Transazioni istantanee, commissioni quasi zero, verificabili on-chain. Il futuro dei pagamenti è qui.'
                  : 'The next-gen stablecoin pegged 1:1 to the dollar, on Polygon. Instant transactions, near-zero fees, verifiable on-chain. The future of payments is here.'
                }
              </p>

              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  { icon: Zap, label: currentLanguage === 'it' ? 'Istantaneo' : 'Instant', desc: currentLanguage === 'it' ? 'Conferma in ~2 secondi' : 'Confirmed in ~2 seconds' },
                  { icon: Shield, label: currentLanguage === 'it' ? 'Garantito' : 'Backed', desc: currentLanguage === 'it' ? 'Riserve reali 1:1' : 'Real 1:1 reserves' },
                  { icon: Globe, label: currentLanguage === 'it' ? 'Globale' : 'Global', desc: '24/7, ' + (currentLanguage === 'it' ? 'ovunque' : 'everywhere') },
                  { icon: TrendingUp, label: currentLanguage === 'it' ? 'Trasparente' : 'Transparent', desc: currentLanguage === 'it' ? 'Verificabile on-chain' : 'Verifiable on-chain' }
                ].map((item, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-xl transition-all ${
                    isDark 
                      ? 'bg-emerald-500/10 border border-emerald-500/20 hover:border-emerald-500/40' 
                      : 'bg-white/80 border border-emerald-100 shadow-sm hover:shadow-md'
                  }`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'
                    }`}>
                      <item.icon className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.label}</p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://getusda.xyz/" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className={`w-full sm:w-auto gap-2 hover:scale-105 transition-all duration-300 ${
                    isDark 
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg shadow-emerald-500/30 text-white' 
                      : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg shadow-emerald-500/25 text-white'
                  }`} data-testid="usda-buy-cta">
                    <Wallet className="w-5 h-5" />
                    {currentLanguage === 'it' ? 'Acquista USDA' : 'Buy USDA'}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
                <Link to="/register">
                  <Button size="lg" variant="outline" className={`w-full sm:w-auto gap-2 ${
                    isDark 
                      ? 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10' 
                      : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                  }`} data-testid="usda-merchant-cta">
                    <CreditCard className="w-5 h-5" />
                    {currentLanguage === 'it' ? 'Attiva per il tuo negozio' : 'Activate for your store'}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Visual */}
            <div className="relative">
              <div className={`absolute inset-0 rounded-3xl ${isDark ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20 blur-3xl' : 'bg-gradient-to-br from-emerald-200 to-teal-200 blur-3xl opacity-50'}`}></div>
              <img 
                src="/usda-hero.png"
                alt="USDA Pay - Crypto Payment Gateway"
                className="relative rounded-3xl shadow-2xl w-full hover:scale-[1.02] transition-transform duration-500"
                style={{ filter: isDark ? 'drop-shadow(0 25px 50px rgba(16, 185, 129, 0.25))' : 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.1))' }}
              />
              {/* Floating badge */}
              <div className={`absolute -bottom-4 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full backdrop-blur-xl ${
                isDark 
                  ? 'bg-[#0F0F2F]/90 border border-emerald-500/40 shadow-xl shadow-emerald-500/20' 
                  : 'bg-white/90 border border-emerald-200 shadow-xl'
              }`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">$</span>
                  </div>
                  <div>
                    <p className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>1 USDA = 1 $</p>
                    <p className={`text-[10px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Polygon Network</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="mt-20">
            <h3 className={`text-center font-heading text-2xl sm:text-3xl font-bold mb-10 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {currentLanguage === 'it' ? 'Come funziona' : 'How it works'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { step: '01', title: currentLanguage === 'it' ? 'Crea pagamento' : 'Create payment', desc: currentLanguage === 'it' ? 'Il merchant crea un link in EUR, USD o GBP' : 'Merchant creates a link in EUR, USD or GBP' },
                { step: '02', title: currentLanguage === 'it' ? 'QR generato' : 'QR generated', desc: currentLanguage === 'it' ? 'Conversione automatica nella valuta → USDA' : 'Automatic currency → USDA conversion' },
                { step: '03', title: currentLanguage === 'it' ? 'Cliente paga' : 'Customer pays', desc: currentLanguage === 'it' ? 'Scansiona con wallet e invia USDA su Polygon' : 'Scan with wallet and send USDA on Polygon' },
                { step: '04', title: currentLanguage === 'it' ? 'Confermato' : 'Confirmed', desc: currentLanguage === 'it' ? 'Verifica automatica on-chain in secondi' : 'Automatic on-chain verification in seconds' }
              ].map((item, i) => (
                <div key={i} className={`text-center p-5 rounded-2xl transition-all ${
                  isDark 
                    ? 'bg-white/5 border border-emerald-500/20 hover:border-emerald-500/40' 
                    : 'bg-white/80 border border-emerald-100 shadow-sm hover:shadow-md'
                }`}>
                  <div className={`text-3xl font-black mb-2 ${isDark ? 'text-emerald-400/30' : 'text-emerald-200'}`}>{item.step}</div>
                  <p className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.title}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-20 px-4 sm:px-6 text-white relative overflow-hidden ${
        isDark 
          ? 'bg-gradient-to-br from-[#12002F] via-[#2A005F] to-[#0B0F2A]' 
          : 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
      }`}>
        <div className="absolute top-0 left-0 w-full h-full">
          {isDark ? (
            <>
              <div className="absolute top-20 left-20 w-[400px] h-[400px] bg-[#6A00FF] rounded-full filter blur-[150px] opacity-20"></div>
              <div className="absolute bottom-20 right-20 w-[500px] h-[500px] bg-[#9D4DFF] rounded-full filter blur-[150px] opacity-20"></div>
            </>
          ) : (
            <>
              <div className="absolute top-20 left-20 w-64 h-64 bg-violet-500/20 rounded-full filter blur-3xl"></div>
              <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/20 rounded-full filter blur-3xl"></div>
            </>
          )}
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className={isDark ? 'mb-6 bg-white/10 text-[#9D4DFF] hover:bg-white/15 border border-purple-500/30' : 'mb-6 bg-white/10 text-white hover:bg-white/20'}>
                <Check className="w-4 h-4 mr-2" />
                Vantaggi
              </Badge>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                {t('landing.benefits.title')}
              </h2>
              <p className={`text-lg mb-10 ${isDark ? 'text-gray-300' : 'text-slate-300'}`}>
                {t('landing.benefits.subtitle')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className={`flex items-center gap-3 p-4 rounded-xl backdrop-blur transition-all duration-300 ${
                    isDark 
                      ? 'bg-white/5 border border-purple-500/20 hover:border-purple-500/40 hover:bg-white/10' 
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg ${
                      isDark 
                        ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/30' 
                        : 'bg-emerald-500'
                    }`}>
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <span className={isDark ? 'text-gray-200' : 'text-slate-200'}>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <Card className={`backdrop-blur-xl text-white overflow-hidden ${
                isDark 
                  ? 'bg-[#0F0F2F]/80 border-purple-500/30 shadow-2xl shadow-purple-500/20' 
                  : 'bg-white/10 border-white/20'
              }`}>
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-8">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${
                      isDark 
                        ? 'bg-gradient-to-br from-[#6A00FF] to-[#9D4DFF] shadow-purple-500/30' 
                        : 'bg-gradient-to-br from-violet-500 to-purple-600'
                    }`}>
                      <Globe className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="font-heading text-xl font-bold">Multi-Gateway</p>
                      <p className={isDark ? 'text-gray-400' : 'text-slate-400'}>{t('landing.features.multiGateway') || 'Integrazione multipla'}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {[
                      { name: 'Gateway 1', status: 'active' },
                      { name: 'Gateway 2', status: 'active' },
                      { name: 'Gateway 3', status: 'active' }
                    ].map((gateway, index) => (
                      <div key={index} className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                        isDark 
                          ? 'bg-white/5 border border-purple-500/20 hover:border-purple-500/40' 
                          : 'bg-white/5 border border-white/10'
                      }`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-lg ${
                            isDark 
                              ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/20' 
                              : 'bg-gradient-to-br from-emerald-400 to-emerald-600'
                          }`}>
                            <Wallet className="w-5 h-5 text-white" />
                          </div>
                          <span className="font-medium">{gateway.name}</span>
                        </div>
                        <Badge className={isDark ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30' : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'}>
                          {t('statuses.active')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 px-4 sm:px-6 ${isDark ? 'bg-[#09081A]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto">
          <Card className={`border-0 overflow-hidden relative ${
            isDark 
              ? 'bg-gradient-to-r from-[#12002F] via-[#6A00FF] to-[#9D4DFF] shadow-2xl shadow-purple-500/30' 
              : 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600'
          }`}>
            <div className={`absolute top-0 right-0 w-[500px] h-[500px] rounded-full -translate-y-1/2 translate-x-1/2 ${isDark ? 'bg-white/10 blur-3xl' : 'bg-white/10'}`}></div>
            <CardContent className="p-12 md:p-16 text-center text-white relative">
              <Sparkles className={`w-12 h-12 mx-auto mb-6 text-yellow-300 ${isDark ? 'drop-shadow-[0_0_20px_rgba(253,224,71,0.5)]' : ''}`} />
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                {t('landing.cta.title')}
              </h2>
              <p className="text-lg text-purple-200 mb-10 max-w-2xl mx-auto">
                {t('landing.cta.subtitle')}
              </p>
              <Link to="/register">
                <Button size="lg" className={`gap-2 px-10 py-6 text-lg font-semibold hover:scale-105 transition-all duration-300 ${
                  isDark 
                    ? 'bg-white text-[#6A00FF] hover:bg-gray-100 shadow-xl shadow-white/20' 
                    : 'bg-white text-purple-600 hover:bg-purple-50 shadow-xl'
                }`} data-testid="cta-register">
                  <Banknote className="w-5 h-5" />
                  {t('landing.cta.button')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* POS Terminals - Coming Soon Section */}
      <section className={`py-20 px-4 sm:px-6 ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className={`absolute inset-0 rounded-3xl ${isDark ? 'bg-gradient-to-br from-[#6A00FF]/30 to-[#9D4DFF]/30 blur-3xl' : 'bg-gradient-to-br from-violet-200 to-purple-200 blur-3xl'}`}></div>
              <img 
                src="/pos-terminal.png"
                alt="AlphaBit POS Terminal"
                className="relative rounded-3xl shadow-2xl w-full max-w-md mx-auto hover:scale-105 transition-transform duration-500"
                style={{ filter: isDark ? 'drop-shadow(0 25px 50px rgba(157, 77, 255, 0.3))' : 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.15))' }}
              />
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <Badge className={`mb-4 ${isDark ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-amber-100 text-amber-700'}`}>
                <Clock className="w-4 h-4 mr-2" />
                {t('landing.pos.badge')}
              </Badge>
              <h2 className={`font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {t('landing.pos.title')}
                <span className={`block ${isDark ? 'text-[#9D4DFF]' : 'text-violet-600'}`}>{t('landing.pos.titleHighlight')}</span>
              </h2>
              <p className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                {t('landing.pos.subtitle')}
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Zap, text: t('landing.pos.feature1') },
                  { icon: Shield, text: t('landing.pos.feature2') },
                  { icon: Globe, text: t('landing.pos.feature3') },
                  { icon: TrendingUp, text: t('landing.pos.feature4') }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-violet-100'}`}>
                      <item.icon className={`w-5 h-5 ${isDark ? 'text-[#9D4DFF]' : 'text-violet-600'}`} />
                    </div>
                    <span className={isDark ? 'text-gray-300' : 'text-slate-700'}>{item.text}</span>
                  </div>
                ))}
              </div>

              <Link to="/register">
                <Button size="lg" className={`gap-2 ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF] shadow-lg shadow-purple-500/30' 
                    : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700'
                }`}>
                  {t('landing.pos.cta')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-12 px-4 sm:px-6 text-white ${
        isDark 
          ? 'bg-[#0B0F2A] border-t border-purple-500/20' 
          : 'bg-slate-900'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="AlphaBit" className={`w-10 h-10 rounded-xl shadow-lg ${isDark ? 'shadow-purple-500/30' : ''}`} />
              <span className="font-heading font-bold text-xl">Alpha<span className={isDark ? 'text-[#9D4DFF]' : 'text-violet-400'}>Bit</span></span>
            </div>
            <div className="flex items-center gap-6">
              {/* WhatsApp */}
              <a 
                href="https://wa.me/393772411743" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`flex items-center gap-2 hover:text-green-400 transition-colors group ${isDark ? 'text-gray-400' : 'text-slate-400'}`}
              >
                <svg className="w-6 h-6 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="text-sm font-medium hidden sm:inline">+39 377 2411743</span>
              </a>
              {/* Telegram */}
              <a 
                href="https://t.me/alphabitpay" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`flex items-center gap-2 hover:text-sky-400 transition-colors group ${isDark ? 'text-gray-400' : 'text-slate-400'}`}
              >
                <svg className="w-6 h-6 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                <span className="text-sm font-medium hidden sm:inline">@alphabitpay</span>
              </a>
            </div>
            <p className="text-sm text-gray-500">
              © 2026 AlphaBit. {t('landing.footer.rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
